import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { catchError, map, merge, mergeMap, of, pipe, switchMap, take, tap } from "rxjs";
import { translate } from '@jsverse/transloco';

import { AppNotificationService } from "src/app/shared/services/notification.service";
import * as CompanyActions from "./company-actions";
import { CompanyService } from "src/app/company/services/company.service";
import { ICompany } from "src/app/company/models/company";

@Injectable()
export class CompaniesEffects {

    ////////////////////
    // GET COMPANIES //
    //////////////////

    // Get Companies 
    getCompanies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CompanyActions.getCompanies), 
            mergeMap(() => {
            return this.companyService.listCompanies({pageNumber: 0, pageSize: 250}).pipe(
                map((response) => 
                    CompanyActions.getCompaniesSuccess({ companies: response.data })
                ), 
                catchError((error) =>
                    of(CompanyActions.getCompaniesFailure({ error: error.message }))
                )
            );
        }),
        take(1)
        )
    );




    //////////////////////
    // CREATE COMPANY  //
    ////////////////////

    // Create Company 
    createCompanies$ = createEffect(() =>
      this.actions$.pipe(
          ofType(CompanyActions.createCompany),
          mergeMap((action) => 
              this.companyService.createCompany(action.company).pipe(
                  map((company: ICompany) => CompanyActions.createCompanySuccess({ company })),
                  catchError((error) => of(CompanyActions.createCompanyFailure({ error: error.message })))
              )
          )
      )
    );

    // Company created successfully
    createCompaniesSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(CompanyActions.createCompanySuccess),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('companies.createSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );

    // Company not created successfully
    createCompaniesFailure$ = createEffect(() => 
      this.actions$.pipe(
          ofType(CompanyActions.createCompanyFailure)
      ), { dispatch: false }
    );




    //////////////////////
    // UPDATE COMPANY  //
    ////////////////////

    // Update Company 
    updateCompanies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CompanyActions.updateCompany),
            mergeMap((action) => 
                this.companyService.updateCompany(action.company).pipe(
                    map((company: ICompany) => CompanyActions.updateCompanySuccess({ company })),
                    catchError((error) => of(CompanyActions.updateCompanyFailure({ error: error.message })))
                )
            )
        )
    );
  
    // Update Company successful
    updateCompaniesSuccess$ = createEffect(() => 
        this.actions$.pipe(
            ofType(CompanyActions.updateCompanySuccess),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('companies.updateSuccess'), style: 'success' });
            })
        ), { dispatch: false }
    );
  
    // Company not updated successfully
    updateCompaniesFailure$ = createEffect(() => 
        this.actions$.pipe(
            ofType(CompanyActions.updateCompanyFailure),
            tap(() => {
                this.appnotificationService.notification$.next({ message:translate('companies.updateFailed'), style: 'error' });
            })
        ), { dispatch: false }
    );




    //////////////////////
    // DELETE COMPANY  //
    ////////////////////

    // Delete Company 
    deleteCompanies$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CompanyActions.deleteCompany), 
            mergeMap((action) => {
                return this.companyService.deleteCompany(action.id).pipe(
                    map(() => CompanyActions.deleteCompanySuccess({id: action.id})), 
                    catchError((error) => of(CompanyActions.deleteCompanyFailure({ error: error.message })))
            )}
        ))
    );

    // Company deleted successfully
    deleteCompaniesSuccess$ = createEffect(() => 
      this.actions$.pipe(
          ofType(CompanyActions.deleteCompanySuccess),
          tap(() => {
              this.appnotificationService.notification$.next({ message:translate('companies.deleteSuccess'), style: 'success' });
          })
      ), { dispatch: false }
    );

    // Company not deleted successfully
    deleteCompaniesFailure$ = createEffect(() =>
      this.actions$.pipe(
        ofType(CompanyActions.deleteCompanyFailure)
      ), { dispatch: false }
    );




    constructor(
        private actions$: Actions, 
        private companyService: CompanyService,
        private appnotificationService: AppNotificationService) {}
}